import { ChangeEvent, ReactElement, ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { regionStore } from '../../store/jotai/store';
import { dsgvoAtom } from '../../store/jotai/atoms/dsgvo.atom';
import { dsgvoService } from '../../services/dsgvo-service';
import './DSGVOOverlay.scss';

const PRIVACY_PATH = '/content/datenschutzerklaerung';
const IMPRINT_PATH = '/content/impressum';

type CategoryProps = {
  title: string;
  /** What the category covers, one per line. */
  items: string[];
  toggle: ReactNode;
};

const Category = ({ title, items, toggle }: CategoryProps): ReactElement => (
  <div className='dsgvo-widget__popup-categories-item'>
    <div className='dsgvo-widget__popup-categories-item-name'>
      <strong>{title}</strong>
      <div className='dsgvo-widget__popup-categories-item-count'>
        {items.map((item) => (
          <div key={item}>- {item}</div>
        ))}
      </div>
    </div>
    <div className='dsgvo-widget__popup-categories-item-toggle'>{toggle}</div>
  </div>
);

export const DSGVOOverlay = (): ReactElement | null => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dsgvo = useAtomValue(dsgvoAtom, { store: regionStore });

  const [performanceCookies, setPerformanceCookies] = useState(dsgvo.performanceCookies);
  const [functionalCookies, setFunctionalCookies] = useState(dsgvo.functionalCookies);
  const [targetingCookies, setTargetingCookies] = useState(dsgvo.targetingCookies);

  // A stored choice replaces the toggles whenever it changes.
  useEffect(() => {
    setPerformanceCookies(dsgvo.performanceCookies);
    setFunctionalCookies(dsgvo.functionalCookies);
    setTargetingCookies(dsgvo.targetingCookies);
  }, [dsgvo]);

  // The pages it links to stay readable.
  if (!dsgvo.showCookieOverlay || pathname === PRIVACY_PATH || pathname === IMPRINT_PATH) {
    return null;
  }

  const save = (performance: boolean, functional: boolean, targeting: boolean): void => {
    dsgvoService.updateCookies(performance, functional, targeting);
    dsgvoService.setCookieOverlayState(false);
  };

  const switchFor = (checked: boolean, onChange: (checked: boolean) => void): ReactElement => (
    <Switch
      checked={checked}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
    />
  );

  return (
    <>
      <div className='dsgvo-widget__popup-bg' />
      <div className='dsgvo-widget__popup'>
        <span className='dsgvo-widget__popup-welcome'>{t('dsgvoOverlay.title')}</span>
        <div className='dsgvo-widget__popup-main'>
          <div className='dsgvo-widget__popup-text'>
            {t('dsgvoOverlay.intro')}
            <br />
            <br />
            <Link to={PRIVACY_PATH}>{t('dsgvoOverlay.moreInfo')}</Link>
            <br />
            <Link to={IMPRINT_PATH}>{t('dsgvoOverlay.imprint')}</Link>
          </div>
          <div className='dsgvo-widget__popup-categories'>
            <Category
              title={t('dsgvoOverlay.essentialCookies')}
              items={[t('dsgvoOverlay.sessionCookies'), t('dsgvoOverlay.loginCookies')]}
              toggle={<Switch disabled defaultChecked />}
            />
            <Category
              title={t('dsgvoOverlay.performanceCookies')}
              items={['Google Analytics']}
              toggle={switchFor(performanceCookies, setPerformanceCookies)}
            />
            <Category
              title={t('dsgvoOverlay.functionalCookies')}
              items={['Google Maps']}
              toggle={switchFor(functionalCookies, setFunctionalCookies)}
            />
            <Category
              title={t('dsgvoOverlay.targetingCookies')}
              items={['Facebook Pixel']}
              toggle={switchFor(targetingCookies, setTargetingCookies)}
            />
            <div className='dsgvo-widget__popup-footer'>
              <Button
                variant='outlined'
                onClick={() => save(performanceCookies, functionalCookies, targetingCookies)}
                className='dsgvo-widget__popup-footer-button'>
                {t('dsgvoOverlay.saveSettings')}
              </Button>
              <Button
                variant='contained'
                onClick={() => save(true, true, true)}
                className='dsgvo-widget__popup-footer-button'>
                {t('dsgvoOverlay.acceptAll')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
